/**
 * Role-Based Access Control (RBAC) Configuration
 *
 * This file defines all roles, permissions, and module access rules for the CAM application.
 *
 * Structure:
 * - ROLES: Role identifiers (stored in localStorage)
 * - PERMISSIONS: Granular permission identifiers
 * - MODULES: Module/route identifiers
 * - ROLE_PERMISSIONS: Mapping of roles to their permissions
 * - MODULE_ACCESS: Mapping of modules to required permissions
 */

// ============================================================================
// ROLE DEFINITIONS
// ============================================================================
// Role names stored in backend/localStorage - can be customized
export const ROLES = {
  PREVIEW: "preview", // Preview Role - can access Data Preview module
  SEARCH: "search", // Search Role - can access Search module
  VALIDATE: "validate", // Validate/Review Role - can access Validate Data module
  VIEWER: "viewer", // Viewer Role - can see all modules
  REVIEWER: "reviewer", // Reviewer Role - can see all except validate
  // Legacy role - maps to all permissions for backward compatibility
  INVESTIGATOR: "investigator",
};

// ============================================================================
// SUB-ROLE DEFINITIONS
// ============================================================================
// Sub-roles that can be assigned in addition to main roles
export const SUB_ROLES = {
  VIEWER: "viewer", // Viewer sub-role - can see Validate button
  REVIEWER: "reviewer", // Reviewer sub-role - cannot see Validate button
};

// ============================================================================
// PERMISSION DEFINITIONS
// ============================================================================
// Granular permissions that can be assigned to roles
export const PERMISSIONS = {
  // Module-level permissions
  VIEW_TASK_STATUS: "view_task_status",
  VIEW_AUDIT_TRAIL: "view_audit_trail",
  VIEW_DATA_PREVIEW: "view_data_preview",
  VIEW_SEARCH: "view_search",
  VIEW_VALIDATE_DATA: "view_validate_data",
  VIEW_VALIDATE_MAPPING: "view_validate_mapping",
  VIEW_VISUALIZATION: "view_visualization",

  // Action-level permissions
  CAN_VALIDATE_BATCH: "can_validate_batch",
  CAN_APPROVE_BATCH: "can_approve_batch",
  CAN_EXPORT_DATA: "can_export_data",
};

// ============================================================================
// MODULE DEFINITIONS
// ============================================================================
// Module identifiers used for navigation and access control
export const MODULES = {
  TASK_STATUS: "task_status",
  AUDIT_TRAIL: "audit_trail",
  SEARCH: "search",
  DATA_PREVIEW: "data_preview",
  VISUALIZATION: "visualization",
  VALIDATE_DATA: "validate_data",
  VALIDATE_MAPPING: "validate_mapping",
};

// ============================================================================
// ROLE-PERMISSION MAPPING
// ============================================================================
// Defines which permissions each role has
export const ROLE_PERMISSIONS = {
  [ROLES.PREVIEW]: [
    PERMISSIONS.VIEW_TASK_STATUS,
    PERMISSIONS.VIEW_AUDIT_TRAIL,
    PERMISSIONS.VIEW_DATA_PREVIEW,
    PERMISSIONS.CAN_EXPORT_DATA,
  ],

  [ROLES.SEARCH]: [
    PERMISSIONS.VIEW_TASK_STATUS,
    PERMISSIONS.VIEW_AUDIT_TRAIL,
    PERMISSIONS.VIEW_SEARCH,
    PERMISSIONS.CAN_EXPORT_DATA,
  ],

  [ROLES.VALIDATE]: [
    PERMISSIONS.VIEW_TASK_STATUS,
    PERMISSIONS.VIEW_AUDIT_TRAIL,
    PERMISSIONS.VIEW_VALIDATE_DATA,
    PERMISSIONS.CAN_VALIDATE_BATCH,
    PERMISSIONS.CAN_APPROVE_BATCH,
    PERMISSIONS.CAN_EXPORT_DATA,
  ],

  // Viewer role - can see all modules except validate
  [ROLES.VIEWER]: [
    PERMISSIONS.VIEW_TASK_STATUS,
    PERMISSIONS.VIEW_AUDIT_TRAIL,
    PERMISSIONS.VIEW_DATA_PREVIEW,
    PERMISSIONS.VIEW_SEARCH,
    PERMISSIONS.VIEW_VISUALIZATION,
    PERMISSIONS.CAN_EXPORT_DATA,
    PERMISSIONS.VIEW_VALIDATE_DATA,
    // Note: VIEWER does NOT have VIEW_VALIDATE_DATA, VIEW_VALIDATE_MAPPING, CAN_VALIDATE_BATCH, or CAN_APPROVE_BATCH
  ],

  // Reviewer role - can see all modules including validate
  [ROLES.REVIEWER]: [
    PERMISSIONS.VIEW_TASK_STATUS,
    PERMISSIONS.VIEW_AUDIT_TRAIL,
    PERMISSIONS.VIEW_DATA_PREVIEW,
    PERMISSIONS.VIEW_SEARCH,
    PERMISSIONS.VIEW_VALIDATE_DATA,
    PERMISSIONS.VIEW_VALIDATE_MAPPING,
    PERMISSIONS.VIEW_VISUALIZATION,
    PERMISSIONS.CAN_EXPORT_DATA,
    // Note: REVIEWER has VIEW_VALIDATE_DATA and VIEW_VALIDATE_MAPPING but may not have CAN_VALIDATE_BATCH or CAN_APPROVE_BATCH
  ],

  // Legacy investigator role - has all permissions for backward compatibility
  [ROLES.INVESTIGATOR]: [
    PERMISSIONS.VIEW_TASK_STATUS,
    PERMISSIONS.VIEW_AUDIT_TRAIL,
    PERMISSIONS.VIEW_DATA_PREVIEW,
    PERMISSIONS.VIEW_SEARCH,
    PERMISSIONS.VIEW_VALIDATE_DATA,
    PERMISSIONS.VIEW_VISUALIZATION,
    PERMISSIONS.CAN_VALIDATE_BATCH,
    PERMISSIONS.CAN_APPROVE_BATCH,
    PERMISSIONS.CAN_EXPORT_DATA,
  ],
};

// ============================================================================
// MODULE-PERMISSION MAPPING
// ============================================================================
// Defines which permission is required to access each module
export const MODULE_ACCESS = {
  [MODULES.TASK_STATUS]: PERMISSIONS.VIEW_TASK_STATUS,
  [MODULES.AUDIT_TRAIL]: PERMISSIONS.VIEW_AUDIT_TRAIL,
  [MODULES.SEARCH]: PERMISSIONS.VIEW_SEARCH,
  [MODULES.DATA_PREVIEW]: PERMISSIONS.VIEW_DATA_PREVIEW,
  [MODULES.VISUALIZATION]: PERMISSIONS.VIEW_VISUALIZATION,
  [MODULES.VALIDATE_DATA]: PERMISSIONS.VIEW_VALIDATE_DATA,
  [MODULES.VALIDATE_MAPPING]: PERMISSIONS.VIEW_VALIDATE_MAPPING,
};

// ============================================================================
// ROUTE-MODULE MAPPING
// ============================================================================
// Maps route paths to their corresponding modules
export const ROUTE_MODULE_MAP = {
  "/task-status": MODULES.TASK_STATUS,
  "/logs": MODULES.AUDIT_TRAIL,
  "/investigation-analysis": MODULES.SEARCH,
  "/files-preview": MODULES.DATA_PREVIEW,
  "/visualization-interface": MODULES.VISUALIZATION,
  "/data-validation": MODULES.VALIDATE_DATA,
  "/validate-data": MODULES.VALIDATE_DATA,
  "/mapping-validation": MODULES.VALIDATE_MAPPING,
  "/validate-mapping": MODULES.VALIDATE_MAPPING,
};

// ============================================================================
// BATCH STATUS FOR VALIDATE BUTTON
// ============================================================================
// Batch statuses that allow validation
export const VALIDATABLE_BATCH_STATUSES = [
  "draft",
  "Draft",
  "pending",
  "Pending",
  "assigned",
  "under_review",
  "Under Review",
  "under review",
];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all permissions for a given role
 * @param {string} role - The role identifier
 * @param {string[]} [subRoles] - Optional array of sub-roles
 * @returns {string[]} - Array of permission identifiers
 */
export const getPermissionsForRole = (role, subRoles = []) => {
  const normalizedRole = role?.toLowerCase();
  const basePermissions = ROLE_PERMISSIONS[normalizedRole] || [];

  // Note: Viewer and Reviewer roles (both main and sub-roles) do NOT get validate permissions
  // This is enforced by canAccessModule which blocks access to VALIDATE_DATA module

  return basePermissions;
};

/**
 * Check if a role has a specific permission
 * @param {string} role - The role identifier
 * @param {string} permission - The permission to check
 * @param {string[]} [subRoles] - Optional array of sub-roles
 * @returns {boolean}
 */
export const hasPermission = (role, permission, subRoles = []) => {
  const permissions = getPermissionsForRole(role, subRoles);
  return permissions.includes(permission);
};

/**
 * Check if a role can access a specific module
 * @param {string} role - The role identifier
 * @param {string} module - The module identifier
 * @param {string[]} [subRoles] - Optional array of sub-roles
 * @returns {boolean}
 */
export const canAccessModule = (role, module, subRoles = []) => {
  if (!role) return false;

  const normalizedRole = role?.toLowerCase()?.trim();
  const requiredPermission = MODULE_ACCESS[module];
  if (!requiredPermission) return true; // No permission required

  // For VALIDATE_DATA and VALIDATE_MAPPING modules, ONLY REVIEWER role can access them
  // All other roles (viewer, validate, investigator, preview, search, etc.) are blocked
  if (module === MODULES.VALIDATE_MAPPING) {
    const normalizedSubRoles = (subRoles || [])
      .map((sr) => sr?.toLowerCase()?.trim())
      .filter(Boolean);

    // // Explicitly check for viewer role and block it first
    // const hasViewerRole =
    //   normalizedRole === ROLES.VIEWER ||
    //   normalizedSubRoles.includes(SUB_ROLES.VIEWER);
    // if (hasViewerRole) {
    //   return false; // Viewer cannot access validate modules
    // }

    // Only reviewer can access validate modules - block all other roles
    const hasReviewerRole =
      normalizedRole === ROLES.REVIEWER ||
      normalizedSubRoles.includes(SUB_ROLES.REVIEWER);
    return hasReviewerRole;
  }

  return hasPermission(role, requiredPermission, subRoles);
};

/**
 * Check if a role can access a specific route
 * @param {string} role - The role identifier
 * @param {string} routePath - The route path
 * @param {string[]} [subRoles] - Optional array of sub-roles
 * @returns {boolean}
 */
export const canAccessRoute = (role, routePath, subRoles = []) => {
  const module = ROUTE_MODULE_MAP[routePath];
  if (!module) return true; // Unknown route - allow access
  return canAccessModule(role, module, subRoles);
};

/**
 * Get all accessible modules for a role
 * @param {string} role - The role identifier
 * @returns {string[]} - Array of module identifiers
 */
export const getAccessibleModules = (role) => {
  return Object.keys(MODULE_ACCESS).filter((module) =>
    canAccessModule(role, module),
  );
};

/**
 * Check if a batch can be validated based on its status
 * @param {string} status - The batch status
 * @returns {boolean}
 */
export const canValidateBatchByStatus = (status) => {
  return VALIDATABLE_BATCH_STATUSES.includes(status);
};

/**
 * Check if user can validate a batch (combines role permission and status check)
 * @param {string} role - The user's role
 * @param {string} batchStatus - The batch status
 * @param {string[]} [subRoles] - Optional array of sub-roles
 * @returns {boolean}
 */
export const canValidateBatch = (role, batchStatus, subRoles = []) => {
  // Only reviewer sub-role can validate, not viewer
  const normalizedSubRoles = subRoles.map((sr) => sr?.toLowerCase());
  const hasReviewerSubRole = normalizedSubRoles.includes(SUB_ROLES.REVIEWER);

  // Must have reviewer sub-role to validate
  if (!hasReviewerSubRole) {
    return false;
  }

  // Check if user has the CAN_VALIDATE_BATCH permission (this ensures proper authorization)
  return (
    hasPermission(role, PERMISSIONS.CAN_VALIDATE_BATCH, subRoles) &&
    canValidateBatchByStatus(batchStatus)
  );
};

/**
 * Get the default route for a role (first accessible module)
 * @param {string} role - The role identifier
 * @returns {string} - The default route path
 */
export const getDefaultRouteForRole = (role) => {
  const accessibleModules = getAccessibleModules(role);

  // Prefer task-status as default if accessible
  if (accessibleModules.includes(MODULES.TASK_STATUS)) {
    return "/task-status";
  }

  // Otherwise return the first accessible route
  const firstModule = accessibleModules[0];
  const routeEntry = Object.entries(ROUTE_MODULE_MAP).find(
    ([, module]) => module === firstModule,
  );
  return routeEntry ? routeEntry[0] : "/";
};

export default {
  ROLES,
  SUB_ROLES,
  PERMISSIONS,
  MODULES,
  ROLE_PERMISSIONS,
  MODULE_ACCESS,
  ROUTE_MODULE_MAP,
  VALIDATABLE_BATCH_STATUSES,
  getPermissionsForRole,
  hasPermission,
  canAccessModule,
  canAccessRoute,
  getAccessibleModules,
  canValidateBatchByStatus,
  canValidateBatch,
  getDefaultRouteForRole,
};
